import React, { FormEvent, useCallback, useEffect, useState } from "react";
import { VelavuAPI, VelavuOrganization } from "velavu-js-api";
import { Trans, useTranslation } from "react-i18next";
import HoverTooltip from "../../elements/hover-tooltip";
import VelavuButton from "../../elements/velavu-button";
import VelavuInput from "../../elements/velavu-input";
import { useVelavuModal } from "../../elements/velavu-modal";
import VelavuNotice from "../../elements/velavu-notice/velavu-notice";
import { useCancellableEffect } from "../../helper/hook-helper";
import { checkEmail } from "../../helper/validation-helper";
import {
	useSignInContext,
	useHasPermission,
} from "../sign-in-context/sign-in-context";
import * as styles from "./settings-organization.module.scss";
import { useVelavuToast } from "../../elements/velavu-toast";
import { ToastNotificationExternalError } from "../../elements/toast-notification-builtins";
import { VelavuScrollableRoundTable } from "../../elements/velavu-scrollable-round-table";
import * as tableStyles from "../../styles/table.module.scss";
import VelavuMenu, {
	VelavuMenuItem,
	VelavuOverflowButton,
} from "../../elements/velavu-menu";
import { getPermissionDeniedMessage } from "../../helper/permission-helper";
import VelavuAlertModalTextConfirm from "../../elements/velavu-alert-modal/velavu-alert-modal-text-confirm";

export default function SettingsOrganization() {
	const { t } = useTranslation();
	const { pushToast } = useVelavuToast();

	const userContext = useSignInContext();
	const updateOrganization = userContext.updateOrganization;
	const userOrganization = userContext.organization?.name;

	const hasOrganizationWritePermission =
		useHasPermission("organization:write");
	const allowChildOrgs = userContext.organization?.allow_child_orgs;
	const canCreateOrganization =
		hasOrganizationWritePermission && allowChildOrgs;

	const [organizationNameInput, setOrganizationNameInput] = useState(
		userContext.organization?.name,
	);
	useEffect(() => {
		setOrganizationNameInput(userOrganization);
	}, [userOrganization, setOrganizationNameInput]);

	const saveOrganizationName = useCallback(async () => {
		//Ignore if we haven't loaded the organization, or the name hasn't changed
		if (
			userOrganization === undefined ||
			userOrganization === organizationNameInput
		) {
			return;
		}

		//Save the organization name
		try {
			await updateOrganization({
				name: organizationNameInput,
			});
		} catch (error) {
			pushToast((close) => (
				<ToastNotificationExternalError onDismiss={close} />
			));
		}
	}, [
		userOrganization,
		organizationNameInput,
		updateOrganization,
		pushToast,
	]);

	//Fetching child organizations
	const [childOrganizations, setChildOrganizations] = useState<
		VelavuOrganization[]
	>([]);

	useCancellableEffect(
		(addPromise) => {
			if (!allowChildOrgs) return;

			addPromise(VelavuAPI.organizations.getChildOrganizations())
				.then(setChildOrganizations)
				.catch((error) => console.log(error.response));
		},
		[allowChildOrgs],
	);

	const addChildOrganization = useCallback(
		(organization: VelavuOrganization) => {
			setChildOrganizations((orgs) => orgs.concat(organization));
		},
		[],
	);

	const { pushModal } = useVelavuModal();

	const removeChildOrganization = useCallback(
		(organization: VelavuOrganization) => {
			pushModal<boolean>((resolve) => (
				<VelavuAlertModalTextConfirm
					title={t("settings.organization.remove_child_org_title")}
					confirmText={organization.name}
					confirmLabel={t("settings.organization.remove")}
					cancelLabel={t("settings.organization.cancel")}
					isDanger
					onResolve={resolve}
				>
					<Trans
						i18nKey="settings.organization.remove_child_org_instruction"
						values={{ name: organization.name }}
						components={{ i: <i /> }}
						parent="span"
					/>
				</VelavuAlertModalTextConfirm>
			)).then((result) => {
				if (!result) return;

				VelavuAPI.organizations
					.deleteChildOrganization(organization.customer_id)
					.then(() =>
						setChildOrganizations((orgs) =>
							orgs.filter(
								(org) =>
									org.customer_id !==
									organization.customer_id,
							),
						),
					)
					.catch(() => {
						pushToast((close) => (
							<ToastNotificationExternalError onDismiss={close} />
						));
					});
			});
		},
		[pushModal, pushToast, t],
	);

	return (
		<div className={styles.container}>
			<OrganizationName
				name={organizationNameInput ?? ""}
				onChange={setOrganizationNameInput}
				onSave={saveOrganizationName}
				disabled={!hasOrganizationWritePermission}
			/>
			{allowChildOrgs ? (
				<>
					<ListChildOrganizations
						organizations={childOrganizations}
						onRemove={removeChildOrganization}
						canDelete={canCreateOrganization}
					/>
					{canCreateOrganization && (
						<CreateChildOrganization onAdd={addChildOrganization} />
					)}
				</>
			) : (
				<div className={styles.listSection}>
					<p className={styles.title}>
						{t("settings.organization.child_organizations_label")}
					</p>
					<div className={styles.blankState}>
						{t("settings.organization.child_orgs_not_allowed")}
					</div>
				</div>
			)}
		</div>
	);
}

interface OrganizationNameProps {
	name: string;
	onChange: (name: string) => void;
	onSave?: () => void;
	disabled?: boolean;
}

function OrganizationName(props: OrganizationNameProps) {
	const { t } = useTranslation();
	return (
		<div className={styles.nameSection}>
			<p className={styles.title}>
				{t("settings.organization.name_label")}
			</p>

			<VelavuInput
				value={props.name}
				onChange={props.onChange}
				onBlur={props.onSave}
				disabled={props.disabled}
				fullWidth
			/>
		</div>
	);
}

function ListChildOrganizations(props: {
	organizations: VelavuOrganization[];
	onRemove: (organization: VelavuOrganization) => void;
	canDelete?: boolean;
}) {
	const { t } = useTranslation();

	return (
		<div className={styles.listSection}>
			<p className={styles.title}>
				{t("settings.organization.child_organizations_label")}
			</p>
			<VelavuScrollableRoundTable
				className={styles.table}
				layout={[
					{ flex: 1 },
					{ flex: 1 },
					{ flex: 1 },
					{ width: 30, alignment: "end" },
				]}
			>
				<thead className={tableStyles.scrollableTableHeader}>
					<tr>
						<th>{t("settings.organization.table_name")}</th>
						<th>{t("settings.organization.table_admin")}</th>
						<th>{t("settings.organization.table_admin_email")}</th>
						<th />
					</tr>
				</thead>

				<tbody>
					{props.organizations.map((organization) => (
						<ChildOrganizationTableRow
							key={organization.customer_id}
							organization={organization}
							disabled={!props.canDelete}
							disabledTooltip={
								!props.canDelete
									? getPermissionDeniedMessage(
											"organization:write",
											t,
										)
									: undefined
							}
							onRemove={() => props.onRemove(organization)}
						/>
					))}
				</tbody>
			</VelavuScrollableRoundTable>
		</div>
	);
}

function ChildOrganizationTableRow(props: {
	organization: VelavuOrganization;
	disabled: boolean;
	disabledTooltip?: string;
	onRemove: VoidFunction;
}) {
	const { t } = useTranslation();

	const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
	const openMenu = useCallback((event: React.MouseEvent<HTMLElement>) => {
		event.stopPropagation();
		setMenuAnchor(event.currentTarget);
	}, []);
	const closeMenu = useCallback(() => {
		setMenuAnchor(null);
	}, []);

	const { onRemove: propsOnRemove } = props;
	const handleRemove = useCallback(() => {
		closeMenu();
		propsOnRemove();
	}, [closeMenu, propsOnRemove]);

	return (
		<>
			<VelavuMenu
				open={!!menuAnchor}
				onClose={closeMenu}
				refPosition={menuAnchor}
				align="right"
			>
				<VelavuMenuItem
					onClick={handleRemove}
					label={t("settings.organization.remove")}
					danger
				/>
			</VelavuMenu>

			<tr className={styles.tableRow}>
				<td className={styles.overflow}>
					<HoverTooltip
						fixed
						tooltip={props.organization.name}
						wrapperClassName={styles.overflowContent}
					>
						{props.organization.name}
					</HoverTooltip>
				</td>
				<td className={styles.overflow}>
					<HoverTooltip
						fixed
						tooltip={props.organization.admin_name}
						wrapperClassName={styles.overflowContent}
					>
						{props.organization.admin_name}
					</HoverTooltip>
				</td>
				<td className={styles.overflow}>
					<HoverTooltip
						fixed
						tooltip={props.organization.admin_email}
						wrapperClassName={styles.overflowContent}
					>
						{props.organization.admin_email}
					</HoverTooltip>
				</td>
				<td>
					<VelavuOverflowButton
						onClick={openMenu}
						open={!!menuAnchor}
						disabled={props.disabled}
						tooltip={props.disabledTooltip}
					/>
				</td>
			</tr>
		</>
	);
}

function CreateChildOrganization(props: {
	onAdd: (organization: VelavuOrganization) => void;
}) {
	const { t } = useTranslation();
	const [nameInput, setNameInput] = useState("");
	const [adminNameInput, setAdminNameInput] = useState("");
	const [adminEmailInput, setAdminEmailInput] = useState("");
	const [isLoading, setLoading] = useState(false);

	const [result, setResult] = useState<
		{ success: boolean; name: string } | undefined
	>(undefined);

	const onAdd = props.onAdd;
	const submit = useCallback(
		(event: FormEvent<HTMLFormElement>) => {
			event.preventDefault();

			setLoading(true);

			VelavuAPI.organizations
				.createChildOrganization({
					name: nameInput,
					admin_name: adminNameInput,
					admin_email: adminEmailInput,
				})
				.then((organization) => {
					setResult({
						success: true,
						name: nameInput,
					});
					setNameInput("");
					setAdminNameInput("");
					setAdminEmailInput("");

					onAdd(organization);
				})
				.catch((error) => {
					console.log(error.response);

					setResult({
						success: false,
						name: nameInput,
					});
				})
				.finally(() => {
					setLoading(false);
				});
		},
		[nameInput, adminNameInput, adminEmailInput, onAdd],
	);

	return (
		<form className={styles.sectionInvite} onSubmit={submit}>
			<span className={styles.title}>
				{t("settings.organization.create_child_org_title")}
			</span>

			<div className={styles.sectionInputs}>
				<VelavuInput
					placeholder={t(
						"settings.organization.child_org_name_placeholder",
					)}
					value={nameInput}
					onChange={setNameInput}
					disabled={isLoading}
					fullWidth
					bottomPadding
				/>

				<VelavuInput
					placeholder={t(
						"settings.organization.admin_name_placeholder",
					)}
					value={adminNameInput}
					onChange={setAdminNameInput}
					disabled={isLoading}
					autoComplete="name"
					fullWidth
					bottomPadding
				/>

				<VelavuInput
					placeholder={t(
						"settings.organization.admin_email_placeholder",
					)}
					value={adminEmailInput}
					onChange={setAdminEmailInput}
					disabled={isLoading}
					autoComplete="email"
					fullWidth
					bottomPadding
				/>
			</div>

			<div className={styles.createActions}>
				{result && (
					<VelavuNotice
						className={styles.statusNotice}
						type={result.success ? "confirm" : "error"}
					>
						{result.success
							? t("settings.organization.child_org_created", {
									name: result.name,
								})
							: t(
									"settings.organization.child_org_create_failed",
									{
										name: result.name,
									},
								)}
					</VelavuNotice>
				)}

				<VelavuButton
					label={t("settings.organization.create")}
					disabled={
						isLoading ||
						nameInput.length === 0 ||
						adminNameInput.length === 0 ||
						!checkEmail(adminEmailInput)
					}
					submit
				/>
			</div>
		</form>
	);
}
